import { useMemo } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../backend/convex/_generated/api';
import { Id } from '../../../backend/convex/_generated/dataModel';
import { extractBookIdFromSlug } from '@/utils/slug';
import { Book, Chapter } from '@/types';

interface UseReaderBookArgs {
  bookIdParam?: string;
  clerkId?: string;
}

interface BookData {
  book: Book;
  chapters: Chapter[];
}

interface UseReaderBookResult {
  actualBookId: string | null;
  bookData: BookData | null | undefined;
  book: Book | undefined;
  chapters: Chapter[];
  sortedChapters: Chapter[];
}

export function useReaderBook({ bookIdParam, clerkId }: UseReaderBookArgs): UseReaderBookResult {
  const shortId = bookIdParam ? extractBookIdFromSlug(bookIdParam) : null;
  const actualBookId = shortId || bookIdParam || null;

  const bookDataById = useQuery(
    api.books.get,
    actualBookId?.startsWith('k')
      ? { id: actualBookId as Id<'books'>, clerkId }
      : 'skip'
  );
  const bookDataBySlug = useQuery(
    api.books.getBySlug,
    actualBookId && !actualBookId.startsWith('k')
      ? { slug: actualBookId, clerkId }
      : 'skip'
  );
  const bookData = (bookDataById || bookDataBySlug) as BookData | null | undefined;

  const book = bookData?.book;
  const chapters = useMemo(() => bookData?.chapters ?? [], [bookData]);
  const sortedChapters = useMemo(
    () => [...chapters].sort((a, b) => a.sequence - b.sequence),
    [chapters]
  );

  return {
    actualBookId,
    bookData,
    book,
    chapters,
    sortedChapters,
  };
}
